import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  UserCircleIcon,
  EnvelopeIcon,
  KeyIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  BellIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import PushNotificationSettings from '../components/notifications/PushNotificationSettings';
import InstallAppCard from '../components/ui/InstallAppCard';
import { useSportDoctorWarningDays } from '../utils/playerMedical';
import { profileMlszAPI } from '../services/api';
import { getErrorMessage } from '../utils/errorMessage';
import MySchoolsCard from '../components/schools/MySchoolsCard';
import MyPlayersCard from '../components/players/MyPlayersCard';
import CalendarFeedCard from '../components/calendar/CalendarFeedCard';
import { PASSWORD_HINT, PASSWORD_MIN_LENGTH, validatePassword } from '../utils/passwordPolicy';

interface ProfileData {
  id: number;
  email: string;
  name: string;
  phone?: string | null;
  isAdmin: boolean;
  isCoach: boolean;
  isPlayer: boolean;
  isParent: boolean;
  isGuest?: boolean;
  guestTeam?: string | null;
  createdAt: string;
  sportDoctorReminderEnabled: boolean;
  weeklySummaryEmailEnabled?: boolean;
  attendanceChangeEmailEnabled?: boolean;
  pushNotificationsEnabled: boolean;
  /** A mentett IFA belépési email cím. A jelszó soha nem jön vissza. */
  mlszEmail?: string | null;
  hasMlszCredentials: boolean;
  /** Van-e a szerveren titkosító kulcs; enélkül nem tárolható IFA jelszó. */
  mlszCredentialStorageAvailable: boolean;
}

interface ProfileProps {
  /**
   * A családi nézet "Fiók és értesítések" oldala: a gyerekek adatai ott a saját
   * aloldalukon vannak, ezért itt nem ismételjük őket.
   */
  familyView?: boolean;
}

const Profile: React.FC<ProfileProps> = ({ familyView = false }) => {
  const sportDoctorWarningDays = useSportDoctorWarningDays();
  const { refreshUser } = useAuth();
  usePageTitle('Profil');
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const guestOnly = !!profile?.isGuest && !profile.isAdmin && !profile.isCoach && !profile.isPlayer && !profile.isParent;
  const [loading, setLoading] = useState(true);

  // Profile form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Notification preferences state
  const [sportDoctorReminderEnabled, setSportDoctorReminderEnabled] = useState(true);
  const [weeklySummaryEmailEnabled, setWeeklySummaryEmailEnabled] = useState(true);
  const [attendanceChangeEmailEnabled, setAttendanceChangeEmailEnabled] = useState(true);
  const [mlszEmail, setMlszEmail] = useState('');
  const [mlszPassword, setMlszPassword] = useState('');
  const [mlszSaving, setMlszSaving] = useState(false);
  const [mlszMessage, setMlszMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleMlszSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setMlszSaving(true);
    setMlszMessage(null);
    try {
      await profileMlszAPI.save(mlszEmail.trim(), mlszPassword);
      setMlszPassword('');
      setProfile((prev) => (prev ? { ...prev, mlszEmail: mlszEmail.trim(), hasMlszCredentials: true } : prev));
      setMlszMessage({ type: 'success', text: 'Az IFA belépési adatok elmentve.' });
    } catch (err) {
      setMlszMessage({ type: 'error', text: getErrorMessage(err, 'Az IFA belépési adatokat nem sikerült elmenteni.') });
    } finally {
      setMlszSaving(false);
    }
  };

  const handleMlszClear = async () => {
    setMlszSaving(true);
    setMlszMessage(null);
    try {
      await profileMlszAPI.clear();
      setMlszEmail('');
      setMlszPassword('');
      setProfile((prev) => (prev ? { ...prev, mlszEmail: null, hasMlszCredentials: false } : prev));
      setMlszMessage({ type: 'success', text: 'Az IFA belépési adatok törölve.' });
    } catch (err) {
      setMlszMessage({ type: 'error', text: getErrorMessage(err, 'A törlés nem sikerült.') });
    } finally {
      setMlszSaving(false);
    }
  };
  const [notificationsSaving, setNotificationsSaving] = useState(false);
  const [notificationsMessage, setNotificationsMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/profile');
      setProfile(response.data);
      setName(response.data.name);
      setEmail(response.data.email);
      setPhone(response.data.phone ?? '');
      setSportDoctorReminderEnabled(response.data.sportDoctorReminderEnabled ?? true);
      setWeeklySummaryEmailEnabled(response.data.weeklySummaryEmailEnabled ?? true);
      setAttendanceChangeEmailEnabled(response.data.attendanceChangeEmailEnabled ?? true);
      setMlszEmail(response.data.mlszEmail ?? '');
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage(null);

    try {
      // Üres szöveg = a telefonszám törlése
      const response = await api.put('/profile', { name, email, phone: phone.trim() });
      setProfile(response.data);
      setProfileMessage({ type: 'success', text: 'Profil sikeresen frissítve!' });

      // Refresh user context to update header
      if (refreshUser) {
        refreshUser();
      }
    } catch (error: any) {
      setProfileMessage({
        type: 'error',
        text: getErrorMessage(error, 'Hiba történt a profil frissítésekor.'),
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const handleNotificationsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotificationsSaving(true);
    setNotificationsMessage(null);

    try {
      const response = await api.put('/profile', {
        sportDoctorReminderEnabled,
        weeklySummaryEmailEnabled,
        attendanceChangeEmailEnabled,
      });
      setProfile(response.data);
      setNotificationsMessage({ type: 'success', text: 'Értesítési beállítások mentve!' });
    } catch (error: any) {
      setNotificationsMessage({
        type: 'error',
        text: getErrorMessage(error, 'Hiba történt a mentés során.'),
      });
    } finally {
      setNotificationsSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSaving(true);
    setPasswordMessage(null);

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Az új jelszavak nem egyeznek.' });
      setPasswordSaving(false);
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setPasswordMessage({ type: 'error', text: passwordError });
      setPasswordSaving(false);
      return;
    }

    try {
      await api.post('/profile/change-password', {
        currentPassword,
        newPassword,
        confirmPassword,
      });
      setPasswordMessage({ type: 'success', text: 'Jelszó sikeresen megváltoztatva!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordMessage({
        type: 'error',
        text: getErrorMessage(error, 'Hiba történt a jelszó módosításakor.'),
      });
    } finally {
      setPasswordSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Page Header */}
      {!familyView && (
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Profil beállítások</h1>
        </div>
      )}

      <div className="space-y-6">
        {/* Profile Info Card */}
        <div className="bg-white shadow-sm rounded-xl overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100 card-header bg-gradient-to-r from-primary-50 to-white">
            <div className="flex items-center space-x-3">
              <div className="card-header-icon p-2 bg-primary-100 rounded-lg">
                <UserCircleIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Személyes adatok</h2>
              </div>
            </div>
          </div>

          <form onSubmit={handleProfileSubmit} className="p-4 md:p-6">
            {profileMessage && (
              <div
                className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                  profileMessage.type === 'success'
                    ? 'bg-green-50 text-green-800'
                    : 'bg-red-50 text-red-800'
                }`}
              >
                {profileMessage.type === 'success' ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                )}
                <span>{profileMessage.text}</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Név
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserCircleIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Teljes név"
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email cím
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="pelda@email.com"
                    required
                  />
                </div>
              </div>

              {/* Telefon: szülőnél ezen éri el az edző a pályáról */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Telefonszám
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={30}
                    autoComplete="tel"
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="+36 30 123 4567"
                  />
                </div>
                {profile?.isParent && (
                  <p className="mt-1 text-xs text-gray-500">Ezen hív az edző, ha a gyermekeddel történik valami.</p>
                )}
              </div>

              {/* Role (read-only) - a családi nézetben nem mond semmit */}
              <div className={familyView ? 'hidden' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Szerepkör
                </label>
                <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 flex flex-wrap gap-2">
                  {profile?.isAdmin && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                      Admin
                    </span>
                  )}
                  {profile?.isCoach && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                      Edző
                    </span>
                  )}
                  {profile?.isPlayer && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      Játékos
                    </span>
                  )}
                  {profile?.isGuest && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-700">
                      {profile.guestTeam ? `Vendég (${profile.guestTeam})` : 'Vendég'}
                    </span>
                  )}
                  {profile?.isParent && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      Szülő
                    </span>
                  )}
                </div>
              </div>

              {/* Created At (read-only) */}
              <div className={familyView ? 'hidden' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Regisztráció dátuma
                </label>
                <div className="px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-600">
                  {profile && new Date(profile.createdAt).toLocaleDateString('hu-HU', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={profileSaving}
                className="w-full md:w-auto px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {profileSaving ? 'Mentés...' : 'Változtatások mentése'}
              </button>
            </div>
          </form>
        </div>

        {/* Elérhetőség, egészségügyi adatok, betegség bejelentése */}
        {!familyView && (profile?.isPlayer || profile?.isParent) && <MyPlayersCard />}

        {/* Iskola: a játékos maga / a szülő a gyermekei iskoláját adja meg */}
        {(profile?.isPlayer || profile?.isParent) && <MySchoolsCard />}

        <CalendarFeedCard />

        {/* Notifications Card - a vendégnek (gondnok, külsős csapat) egyik levél sem szól */}
        {!guestOnly && (
        <div className="bg-white shadow-sm rounded-xl overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100 card-header bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-center space-x-3">
              <div className="card-header-icon p-2 bg-blue-100 rounded-lg">
                <BellIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Email értesítések</h2>
              </div>
            </div>
          </div>

          <form onSubmit={handleNotificationsSubmit} className="p-4 md:p-6">
            {notificationsMessage && (
              <div
                className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                  notificationsMessage.type === 'success'
                    ? 'bg-green-50 text-green-800'
                    : 'bg-red-50 text-red-800'
                }`}
              >
                {notificationsMessage.type === 'success' ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                )}
                <span>{notificationsMessage.text}</span>
              </div>
            )}

            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={sportDoctorReminderEnabled}
                onChange={(e) => setSportDoctorReminderEnabled(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <div>
                <div className="font-medium text-gray-900">Sportorvosi lejárat emlékeztető</div>
                <div className="text-sm text-gray-500">
                  Email, amikor egy általad látható játékos sportorvosi érvényessége{' '}
                  {sportDoctorWarningDays > 30
                    ? `${sportDoctorWarningDays}, 30 vagy 7`
                    : sportDoctorWarningDays > 7
                      ? `${sportDoctorWarningDays} vagy 7`
                      : '7'}{' '}
                  napon belül lejár, illetve amikor lejárt (lejárt igazolásról 4 hetente újra).
                </div>
              </div>
            </label>

            {(profile?.isAdmin || profile?.isCoach) && (
              <>
                <label className="mt-4 flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={weeklySummaryEmailEnabled}
                    onChange={(e) => setWeeklySummaryEmailEnabled(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Heti összefoglaló</div>
                    <div className="text-sm text-gray-500">
                      Vasárnap este email a következő hét eseményeiről a korosztályaidban.
                    </div>
                  </div>
                </label>

                <label className="mt-4 flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={attendanceChangeEmailEnabled}
                    onChange={(e) => setAttendanceChangeEmailEnabled(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">Visszajelzés-változás</div>
                    <div className="text-sm text-gray-500">
                      Email, amikor egy játékos vagy szülő megváltoztatja a korábbi visszajelzését egy eseményre a
                      korosztályaidban.
                    </div>
                  </div>
                </label>
              </>
            )}

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={notificationsSaving}
                className="w-full md:w-auto px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {notificationsSaving ? 'Mentés...' : 'Mentés'}
              </button>
            </div>
          </form>
        </div>
        )}

        {/* IFA belépés - a sportorvosi dátumok frissítéséhez */}
        {(profile?.isAdmin || profile?.isCoach) && (
          <div className="bg-white shadow-sm rounded-xl overflow-hidden">
            <div className="px-4 md:px-6 py-4 border-b border-gray-100 card-header bg-gradient-to-r from-blue-50 to-white">
              <div className="flex items-center space-x-3">
                <div className="card-header-icon p-2 bg-blue-100 rounded-lg">
                  <KeyIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">IFA belépés</h2>
                  <p className="text-sm text-gray-500">
                    A sportorvosi dátumok IFA-ból frissítéséhez a saját MLSZ belépésed kell.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleMlszSave} className="p-4 md:p-6">
              {mlszMessage && (
                <div
                  className={`mb-4 p-4 rounded-lg flex items-center space-x-3 ${
                    mlszMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                  }`}
                >
                  {mlszMessage.type === 'success' ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                  )}
                  <span>{mlszMessage.text}</span>
                </div>
              )}

              {profile?.mlszCredentialStorageAvailable === false ? (
                <p className="text-sm text-amber-700">
                  A szerveren nincs beállítva titkosító kulcs, ezért az IFA jelszó nem tárolható.
                  Keresd a rendszergazdát - addig a sportorvosi dátumot kézzel lehet megadni.
                </p>
              ) : (
                <>
                  <p className="mb-4 text-sm text-gray-500">
                    A jelszót titkosítva tároljuk, és semmilyen felületen nem olvasható vissza -
                    csak az IFA felé küldjük el. Bármikor törölheted.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="mlsz-email" className="block text-sm font-medium text-gray-700 mb-1">
                        IFA email cím
                      </label>
                      <input
                        type="email"
                        id="mlsz-email"
                        autoComplete="off"
                        value={mlszEmail}
                        onChange={(e) => setMlszEmail(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="mlsz-password" className="block text-sm font-medium text-gray-700 mb-1">
                        IFA jelszó
                      </label>
                      <input
                        type="password"
                        id="mlsz-password"
                        autoComplete="new-password"
                        value={mlszPassword}
                        onChange={(e) => setMlszPassword(e.target.value)}
                        placeholder={profile?.hasMlszCredentials ? 'Mentve - új megadásával felülírod' : ''}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap justify-end gap-2">
                    {profile?.hasMlszCredentials && (
                      <button
                        type="button"
                        onClick={handleMlszClear}
                        disabled={mlszSaving}
                        className="px-6 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
                      >
                        Mentett adatok törlése
                      </button>
                    )}
                    <button
                      type="submit"
                      disabled={mlszSaving || !mlszEmail.trim() || !mlszPassword}
                      className="px-6 py-2.5 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {mlszSaving ? 'Mentés...' : 'Mentés'}
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        )}

        {/* Install Card - iPhone-on ez az értesítések előfeltétele */}
        <InstallAppCard />

        {/* Push Notifications Card */}
        <PushNotificationSettings initialEnabled={profile?.pushNotificationsEnabled ?? true} />

        {/* Password Change Card */}
        <div className="bg-white shadow-sm rounded-xl overflow-hidden">
          <div className="px-4 md:px-6 py-4 border-b border-gray-100 card-header bg-gradient-to-r from-amber-50 to-white">
            <div className="flex items-center space-x-3">
              <div className="card-header-icon p-2 bg-amber-100 rounded-lg">
                <KeyIcon className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Jelszó módosítása</h2>
              </div>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="p-4 md:p-6">
            {passwordMessage && (
              <div
                className={`mb-6 p-4 rounded-lg flex items-center space-x-3 ${
                  passwordMessage.type === 'success'
                    ? 'bg-green-50 text-green-800'
                    : 'bg-red-50 text-red-800'
                }`}
              >
                {passwordMessage.type === 'success' ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500" />
                ) : (
                  <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                )}
                <span>{passwordMessage.text}</span>
              </div>
            )}

            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Jelenlegi jelszó
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <KeyIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="password"
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Add meg a jelenlegi jelszavad"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* New Password */}
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Új jelszó
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder={PASSWORD_HINT}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                  />
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Új jelszó megerősítése
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                    placeholder="Írd be újra az új jelszót"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                disabled={passwordSaving}
                className="w-full md:w-auto px-6 py-2.5 bg-amber-600 text-white font-medium rounded-lg hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {passwordSaving ? 'Mentés...' : 'Jelszó módosítása'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
